import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Shield, Globe, Award, Heart, Scroll, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { productsApi, newsletterApi } from '../services/api';
import { toast } from 'sonner';

const features = [
  {
    id: 1,
    title: '100% Authentic',
    description: 'Genuine ingredients sourced directly from India',
    icon: Shield,
  },
  {
    id: 2,
    title: 'No Preservatives',
    description: 'Natural goodness without any chemicals',
    icon: Leaf,
  },
  {
    id: 3,
    title: 'Heritage Recipes',
    description: 'Passed down through generations of Marwari families',
    icon: Scroll,
  },
  {
    id: 4,
    title: 'Handcrafted',
    description: 'Made in small batches with love and care',
    icon: Heart,
  },
  {
    id: 5,
    title: 'Global Shipping',
    description: 'Delivering to Singapore, Dubai, Australia, UK & India',
    icon: Globe,
  },
  {
    id: 6,
    title: 'Premium Quality',
    description: 'Only the finest ingredients make the cut',
    icon: Award,
  },
];

const testimonials = [
  {
    id: 1,
    name: 'Priya Sharma',
    location: 'Singapore',
    text: "Finally found authentic Marwari pickle that tastes exactly like my grandmother's! The aam ka achaar is incredible.",
    rating: 5,
    avatar: 'PS',
  },
  {
    id: 2,
    name: 'Rajesh Agarwal',
    location: 'Dubai',
    text: "The cold pressed mustard oil brings back childhood memories. Quality is unmatched compared to what's available here.",
    rating: 5,
    avatar: 'RA',
  },
  {
    id: 3,
    name: 'Anita Gupta',
    location: 'London',
    text: "Best makhana I've had outside India. Perfectly crunchy and fresh. My kids love the roasted snack version!",
    rating: 5,
    avatar: 'AG',
  },
  {
    id: 4,
    name: 'Vikram Jain',
    location: 'Melbourne',
    text: 'The heritage pickle combo was a hit at our Diwali party. Everyone asked where I got such authentic taste from!',
    rating: 5,
    avatar: 'VJ',
  },
];

const heroImage = 'https://images.unsplash.com/photo-1728910156510-77488f19b152?w=1920&auto=format&fit=crop';

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productsApi.getAll(),
        productsApi.getCategories(),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await newsletterApi.subscribe(email);
      toast.success('Successfully subscribed!', {
        description: "You'll receive exclusive offers and updates.",
      });
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const featuredProducts = products.slice(0, 4);

  return (
    <div className="min-h-screen bg-[#FDF6E3]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Heritage Indian Food"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3D2314]/95 via-[#3D2314]/80 to-[#3D2314]/40" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-[#D4A853]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#C17817]/10 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4A853]/20 backdrop-blur-sm px-4 py-2 rounded-full mb-8 border border-[#D4A853]/30">
              <span className="w-2 h-2 bg-[#D4A853] rounded-full animate-pulse" />
              <span className="text-[#D4A853] text-sm font-medium tracking-wide">
                Now shipping to Singapore, Dubai, Australia, UK & India
              </span>
            </div>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-[#FDF6E3] leading-tight mb-6">
              Heritage Taste,
              <span className="block text-[#D4A853]">Authentic Soul</span>
            </h1>

            <p className="text-xl text-[#FDF6E3]/80 leading-relaxed mb-10 max-w-xl">
              Experience the timeless flavors of Marwari tradition. 100% authentic, 
              handcrafted products with no preservatives — just pure heritage goodness.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button className="bg-[#C17817] hover:bg-[#D4A853] text-[#FDF6E3] px-8 py-6 text-lg font-medium rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 group">
                  Explore Products
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-[#FDF6E3]/30 text-[#FDF6E3] hover:bg-[#FDF6E3]/10 hover:border-[#FDF6E3]/50 px-8 py-6 text-lg font-medium rounded-xl backdrop-blur-sm">
                  Our Story
                </Button>
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-6 mt-12 pt-8 border-t border-[#FDF6E3]/20">
              <div className="text-center">
                <p className="text-3xl font-bold text-[#D4A853]">100%</p>
                <p className="text-xs text-[#FDF6E3]/60 uppercase tracking-wider">Authentic</p>
              </div>
              <div className="w-px h-12 bg-[#FDF6E3]/20" />
              <div className="text-center">
                <p className="text-3xl font-bold text-[#D4A853]">Zero</p>
                <p className="text-xs text-[#FDF6E3]/60 uppercase tracking-wider">Preservatives</p>
              </div>
              <div className="w-px h-12 bg-[#FDF6E3]/20" />
              <div className="text-center">
                <p className="text-3xl font-bold text-[#D4A853]">5</p>
                <p className="text-xs text-[#FDF6E3]/60 uppercase tracking-wider">Countries</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C17817] font-medium text-sm tracking-widest uppercase">Our Collection</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#5C3D2E] mt-3">
              Explore Categories
            </h2>
            <p className="text-[#5C3D2E]/60 mt-4 max-w-2xl mx-auto">
              Handpicked products that bring the authentic taste of Marwari tradition to your kitchen
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-[#C17817]" />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to="/products"
                  className="group relative aspect-[4/5] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3D2314]/90 via-[#3D2314]/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif text-xl font-bold text-[#FDF6E3] mb-1 group-hover:text-[#D4A853] transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-[#FDF6E3]/70 text-sm">{category.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-[#FDF6E3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12 gap-4">
            <div>
              <span className="text-[#C17817] font-medium text-sm tracking-widest uppercase">Bestsellers</span>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#5C3D2E] mt-3">
                Most Loved Products
              </h2>
            </div>
            <Link to="/products">
              <Button variant="outline" className="border-2 border-[#C17817] text-[#C17817] hover:bg-[#C17817] hover:text-[#FDF6E3] rounded-xl px-6 group">
                View All
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-[#C17817]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#3D2314] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C17817]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4A853]/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4A853] font-medium text-sm tracking-widest uppercase">Why House of Bania</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#FDF6E3] mt-3">
              The Heritage Promise
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={feature.id}
                  className="bg-[#FDF6E3]/5 backdrop-blur-sm rounded-2xl p-8 border border-[#D4A853]/20 hover:border-[#D4A853]/40 transition-all duration-300 hover:transform hover:-translate-y-1"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#C17817] to-[#D4A853] flex items-center justify-center mb-6 shadow-lg">
                    <IconComponent className="h-7 w-7 text-[#FDF6E3]" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#FDF6E3] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-[#FDF6E3]/60 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C17817] font-medium text-sm tracking-widest uppercase">Testimonials</span>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#5C3D2E] mt-3">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-[#FDF6E3] rounded-2xl p-6 border border-[#D4A853]/20 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-4 h-4 text-[#D4A853] fill-[#D4A853]"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[#5C3D2E]/80 text-sm leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C17817] to-[#D4A853] flex items-center justify-center">
                    <span className="text-[#FDF6E3] text-sm font-bold">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <p className="font-medium text-[#5C3D2E] text-sm">{testimonial.name}</p>
                    <p className="text-[#5C3D2E]/50 text-xs">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-gradient-to-br from-[#C17817] to-[#8B4513] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border border-[#FDF6E3] rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border border-[#FDF6E3] rounded-full" />
          <div className="absolute top-1/2 left-1/2 w-80 h-80 border border-[#FDF6E3] rounded-full transform -translate-x-1/2 -translate-y-1/2" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#FDF6E3] mb-4">
            Join the Bania Family
          </h2>
          <p className="text-[#FDF6E3]/80 text-lg mb-10 max-w-2xl mx-auto">
            Subscribe for exclusive offers, new product launches, and authentic recipes from our heritage kitchen.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="flex-1 px-6 py-4 rounded-xl bg-[#FDF6E3] text-[#5C3D2E] placeholder:text-[#5C3D2E]/50 focus:outline-none focus:ring-2 focus:ring-[#D4A853] shadow-lg"
            />
            <Button 
              type="submit"
              disabled={subscribing}
              className="bg-[#3D2314] hover:bg-[#5C3D2E] text-[#FDF6E3] px-8 py-4 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {subscribing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Subscribe'
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
